import React, { createContext, useContext, useState, ReactNode } from 'react';
import { EventFilters } from '../services/eventService';

interface FilterContextType {
  activeFilter: string; // For backward compatibility - represents event filter
  activeEventFilter: string;
  activeCategoryFilter: string | null;
  filters: EventFilters;
  selectedCity: string | null;
  setActiveFilter: (filter: string) => void;
  setActiveEventFilter: (filter: string) => void;
  setActiveCategoryFilter: (categoryId: string | null) => void;
  setSelectedCity: (cityId: string | null) => void;
  clearFilters: () => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export function useFilters(): FilterContextType {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
}

interface FilterProviderProps {
  children: ReactNode;
}

export function FilterProvider({ children }: FilterProviderProps) {
  const [activeEventFilter, setActiveEventFilterState] = useState<string>('All Events');
  const [activeCategoryFilter, setActiveCategoryFilterState] = useState<string | null>(null);
  const [selectedCity, setSelectedCityState] = useState<string | null>(null);
  const [filters, setFilters] = useState<EventFilters>({});

  // For backward compatibility
  const activeFilter = activeEventFilter;

  const updateFilters = (eventFilter: string, categoryId: string | null, cityId: string | null) => {
    const newFilters: EventFilters = {};

    // Apply event filter
    switch (eventFilter) {
      case 'All Events':
        // No filters
        break;
      case 'Free Events':
        newFilters.isFree = true;
        break;
      case 'Today':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const endOfDay = new Date(today);
        endOfDay.setHours(23, 59, 59, 999);
        newFilters.dateRange = {
          start: today,
          end: endOfDay
        };
        break;
      case 'This Weekend':
        const now = new Date();
        const dayOfWeek = now.getDay();
        const daysUntilSaturday = (6 - dayOfWeek) % 7;
        const saturday = new Date(now);
        saturday.setDate(now.getDate() + daysUntilSaturday);
        saturday.setHours(0, 0, 0, 0);

        const sunday = new Date(saturday);
        sunday.setDate(saturday.getDate() + 1);
        sunday.setHours(23, 59, 59, 999);

        newFilters.dateRange = {
          start: saturday,
          end: sunday
        };
        break;
      case 'Online Events':
        newFilters.tags = ['online', 'virtual', 'remote'];
        break;
    }

    // Apply category filter
    if (categoryId) {
      newFilters.category = categoryId;
    }

    // Apply city filter
    if (cityId) {
      newFilters.city = cityId;
    }

    setFilters(newFilters);
  };

  const setActiveEventFilter = (filter: string) => {
    setActiveEventFilterState(filter);
    updateFilters(filter, activeCategoryFilter, selectedCity);
  };

  const setActiveCategoryFilter = (categoryId: string | null) => {
    setActiveCategoryFilterState(categoryId);
    updateFilters(activeEventFilter, categoryId, selectedCity);
  };

  // Backward compatibility - sets event filter
  const setActiveFilter = (filter: string) => {
    setActiveEventFilter(filter);
  };

  const setSelectedCity = (cityId: string | null) => {
    setSelectedCityState(cityId);
    updateFilters(activeEventFilter, activeCategoryFilter, cityId);
  };

  const clearFilters = () => {
    setActiveEventFilterState('All Events');
    setActiveCategoryFilterState(null);
    setSelectedCityState(null);
    setFilters({});
  };

  const contextValue: FilterContextType = {
    activeFilter,
    activeEventFilter,
    activeCategoryFilter,
    filters,
    selectedCity,
    setActiveFilter,
    setActiveEventFilter,
    setActiveCategoryFilter,
    setSelectedCity,
    clearFilters
  };

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
}